import { createContext, lazy, Suspense, useContext, useEffect, useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router';
import { startAnalytics, userIdFromToken } from './lib/analytics';
import { UpgradeProvider } from './pages/PlanGate';
import { graphql, SHOP_QUERY, type ShopInfo } from './lib/api';
import { reportError, setErrorShop } from './lib/errors';
import { loadInfo as loadUgcInfo } from './lib/ugc';
import { loadInfo as loadCommentsInfo } from './lib/comments';
import { ENGLISH, langFrom, loadStrings, type Lang, type Strings } from './lib/i18n';
import { CONNECT_EVENT, type ConnectEventDetail } from './lib/instagramConnect';
import { showError } from './lib/notify';

// Every page loads on demand to keep the first bundle small (admin LCP ≤ 2.5 s).
const Home = lazy(() => import('./pages/home/Home'));
const About = lazy(() => import('./pages/About'));
const Posts = lazy(() => import('./pages/posts/Posts'));
const TagEditor = lazy(() => import('./pages/posts/TagEditor'));
const Settings = lazy(() => import('./pages/settings/Settings'));
const Plans = lazy(() => import('./pages/plans/Plans'));
const SYNC_PLAN = `mutation { syncPlan }`;
const Analytics = lazy(() => import('./pages/analytics/Analytics'));
const Feeds = lazy(() => import('./pages/feeds/Feeds'));
const Files = lazy(() => import('./pages/files/Files'));
const CustomerPosts = lazy(() => import('./pages/customers/CustomerPosts'));
const Comments = lazy(() => import('./pages/comments/Comments'));
const FeedEditor = lazy(() => import('./pages/feeds/FeedEditor'));

type AppState = { info: ShopInfo; t: Strings; lang: Lang; setSetupDone: (done: boolean) => void };
const AppContext = createContext<AppState | null>(null);

export function useApp(): AppState {
  const value = useContext(AppContext);
  if (!value) throw new Error('useApp outside AppContext');
  return value;
}

/** App Bridge sends sidebar clicks as `shopify:navigate` events; route them through React Router. */
function useAdminNavigation() {
  const navigate = useNavigate();
  useEffect(() => {
    const onNavigate = (event: Event) => {
      const href = (event.target as HTMLElement | null)?.getAttribute?.('href');
      if (href) navigate(href);
    };
    document.addEventListener('shopify:navigate', onNavigate);
    return () => document.removeEventListener('shopify:navigate', onNavigate);
  }, [navigate]);
}

/** A popup Instagram connect finished (lib/instagramConnect): one toast, and on success the pages load again
 * from Home (the `epoch` key remounts them), like coming back from Instagram in the same window. */
function useInstagramConnected(t: Strings | null) {
  const navigate = useNavigate();
  const [epoch, setEpoch] = useState(0);
  useEffect(() => {
    if (!t) return;
    const onConnect = (event: Event) => {
      const { result } = (event as CustomEvent<ConnectEventDetail>).detail;
      if (result !== 'connected') {
        showError(t.connectError);
        return;
      }
      shopify.toast.show(t.instagramConnected);
      setEpoch(n => n + 1);
      navigate('/');
    };
    window.addEventListener(CONNECT_EVENT, onConnect);
    return () => window.removeEventListener(CONNECT_EVENT, onConnect);
  }, [t, navigate]);
  return epoch;
}

export function App() {
  useAdminNavigation();
  const lang = langFrom(new URLSearchParams(window.location.search).get('locale'));
  const [loaded, setLoaded] = useState<Strings | null>(lang === 'en' ? ENGLISH : null);
  const epoch = useInstagramConnected(loaded);
  const [info, setInfo] = useState<ShopInfo | null>(null);
  const [failed, setFailed] = useState(false);
  const [setupDone, setSetupDone] = useState(false);
  // Customer posts (7b): hidden until the feature is switched on; waiting count next to the menu item,
  // refreshed on every page change.
  const [ugcAvailable, setUgcAvailable] = useState(false);
  const [waiting, setWaiting] = useState(0);
  // Comments inbox (7d): same idea, the open questions count next to the menu item.
  const [commentsAvailable, setCommentsAvailable] = useState(false);
  const [questions, setQuestions] = useState(0);
  const { pathname } = useLocation();
  useEffect(() => {
    if (!setupDone) return;
    loadUgcInfo()
      .then(info => {
        setUgcAvailable(info.available);
        setWaiting(info.available && info.allowed ? info.pending : 0);
      })
      .catch(() => undefined);
    loadCommentsInfo()
      .then(info => {
        setCommentsAvailable(info.available);
        setQuestions(info.available && info.allowed ? info.questions : 0);
      })
      .catch(() => undefined);
  }, [setupDone, pathname]);

  // French and Spanish load in parallel with the shop data; English if that download fails.
  useEffect(() => {
    if (lang === 'en') return;
    loadStrings(lang)
      .then(setLoaded)
      .catch(error => {
        reportError(error, { source: 'load-strings' });
        setLoaded(ENGLISH);
      });
  }, [lang]);

  useEffect(() => {
    let active = true;
    // Back from Shopify's plan selection page (`?plan_handle=`): check the new plan before showing it.
    const returning = new URLSearchParams(window.location.search).has('plan_handle');
    (returning ? graphql(SYNC_PLAN).catch(error => reportError(error, { source: 'sync-plan' })) : Promise.resolve())
      .then(() => graphql<ShopInfo>(SHOP_QUERY))
      .then(async data => {
        if (!active) return;
        setInfo(data);
        setSetupDone(data.setup.done);
        setErrorShop(data.shop.id, data.shop.plan.code);
        const userId = userIdFromToken(await shopify.idToken());
        startAnalytics({ shopId: data.shop.id, shopDomain: data.shop.domain, userId, plan: data.shop.plan.code });
      })
      .catch(error => {
        reportError(error, { source: 'shop-query' });
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, []);

  if (failed) return <s-banner tone="critical">{(loaded ?? ENGLISH).error}</s-banner>;
  if (!info || !loaded) return <PageLoader label={(loaded ?? ENGLISH).loading} />;
  const t = loaded;

  return (
    <AppContext.Provider value={{ info, t, lang, setSetupDone }}>
      {/* Until setup is done only Plans shows (design 1); then Posts, Settings, Plans; the app name is Home (design 3e). */}
      <s-app-nav>
        {/* No Home item: the app name already opens "/" (Built for Shopify 4.1.4). */}
        {setupDone && <s-link href="/posts">{t.postsTitle}</s-link>}
        {setupDone && ugcAvailable && (
          <s-link href="/customer-posts">{waiting > 0 ? `${t.customerPosts} (${waiting})` : t.customerPosts}</s-link>
        )}
        {setupDone && commentsAvailable && (
          <s-link href="/comments">{questions > 0 ? `${t.comments} (${questions})` : t.comments}</s-link>
        )}
        {setupDone && <s-link href="/feeds">{t.feeds}</s-link>}
        {setupDone && <s-link href="/files">{t.files}</s-link>}
        {setupDone && <s-link href="/analytics">{t.analytics}</s-link>}
        {setupDone && <s-link href="/settings">{t.settings}</s-link>}
        <s-link href="/plans">{t.plans}</s-link>
      </s-app-nav>
      <UpgradeProvider>
      <Suspense fallback={<PageLoader label={t.loading} />}>
        <Routes key={epoch}>
          <Route path="/" element={<Home />} />
          <Route path="/posts" element={<Posts />} />
          <Route path="/posts/:id/tags" element={<TagEditor />} />
          <Route path="/plans" element={<Plans />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/feeds" element={<Feeds />} />
          <Route path="/files" element={<Files />} />
          <Route path="/customer-posts" element={<CustomerPosts />} />
          <Route path="/comments" element={<Comments />} />
          <Route path="/feeds/:id" element={<FeedEditor />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/about" element={<About />} />
          <Route path="*" element={<Home />} />
        </Routes>
      </Suspense>
      </UpgradeProvider>
    </AppContext.Provider>
  );
}

/** Full-page spinner, centred in the app frame while the app or a page bundle loads. */
function PageLoader({ label }: { label: string }) {
  return (
    <div className="ux-loader">
      <s-spinner accessibilityLabel={label} size="large" />
    </div>
  );
}
